'use client';
import React, { useState, useEffect } from 'react';
import { siteSettings, navLinks } from '@/data/siteData';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoImage, setLogoImage] = useState<string>('');
  const [logoLoading, setLogoLoading] = useState<boolean>(true);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    // Set initial value from siteSettings on client only
    setLogoImage(siteSettings?.logoImage || '');
    fetch('/api/settings')
      .then((res) => res.json())
      .then((data) => {
        if (data?.settings?.logoImage) {
          setLogoImage(data.settings.logoImage);
        }
      })
      .catch(() => {})
      .finally(() => setLogoLoading(false));
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: { href: string; external?: boolean }) => {
    if (!link.external && link.href.startsWith('#')) {
      e.preventDefault();
      const target = document.querySelector(link.href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
      setMobileOpen(false);
    }
  };

  return (
    <nav className="w-full">
      <div className="px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            {!isMounted || logoLoading ? (
              <div className="h-10 w-28 bg-gray-200 animate-pulse rounded-md" />
            ) : logoImage ? (
              <img
                className="h-10 w-auto"
                src={logoImage}
                alt="Fajrul.id Logo"
              />
            ) : (
              <span className="h-10 w-auto block" />
            )}
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {navLinks?.map((link) => (
                <a
                  key={link?.label}
                  href={link?.href}
                  target={link?.external ? '_blank' : undefined}
                  rel={link?.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleNavClick(e, link)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    link?.label === 'Home' ? 'text-gray-900 font-bold' : 'text-gray-500 hover:text-gray-900'
                  }`}
                  aria-current={link?.label === 'Home' ? 'page' : undefined}
                >
                  {link?.label}
                </a>
              ))}
            </div>
          </div>
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-900 focus:outline-none"
              type="button"
              aria-expanded={mobileOpen}
            >
              <span className="sr-only">Open main menu</span>
              {mobileOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="block h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="block h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white rounded-lg mx-4 mt-2 shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                target={link?.external ? '_blank' : undefined}
                rel={link?.external ? 'noopener noreferrer' : undefined}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                onClick={(e) => handleNavClick(e, link)}
              >
                {link?.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
